import { useEffect } from 'react';

import AsyncStorage from '@react-native-async-storage/async-storage';

import { LeagueConfigsFragment } from '@/api/leagues/query.generated';
import { useLeaguesConfigs } from '@/hooks/use-fantasy-league-configs';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { League } from '@/types/api.generated';
import isEqual from 'lodash/isEqual';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type LeagueConfigStore = {
    leagueConfigs: LeagueConfigsFragment[];
    actions: {
        setLeagueConfigs: (value: LeagueConfigsFragment[]) => void;
    };
};

export const leagueConfigSelector = (league?: League) => (state: LeagueConfigStore) => {
    const leagueConfig = state.leagueConfigs.find(it => it.league === league);
    if (!league || !leagueConfig) {
        return { leagueColor: undefined, leagueIcon: undefined, leagueLabel: undefined };
    }
    return {
        leagueColor: leagueConfig.playerProfileBackgroundColor,
        leagueIcon: leagueConfig.icon,
        leagueLabel: leagueConfig.label,
    };
};

export const useLeagueConfigsStore = create<LeagueConfigStore>()(
    persist(
        (set, _get) => ({
            leagueConfigs: [],
            actions: {
                setLeagueConfigs: value => {
                    if (isEqual(_get().leagueConfigs, value)) {
                        return;
                    }
                    set({ leagueConfigs: value });
                },
            },
        }),
        {
            storage: createJSONStorage(() => AsyncStorage),
            name: 'league-configs',
            version: 1, // eslint-disable-next-line @typescript-eslint/no-unused-vars
            partialize: ({ actions, ...rest }: LeagueConfigStore) => rest,
        }
    )
);

/**
 * Hook to fetch and store league configs in the zustand store
 */
export const useUpdatePickemLeagueConfigs = () => {
    const product = useJurisdictionStore(store => store.product) ?? '';
    const [{ data }] = useLeaguesConfigs({ pause: product !== Product.Pickem });
    const setLeagueConfigs = useLeagueConfigsStore(state => state.actions.setLeagueConfigs);

    useEffect(() => {
        if (data?.getLeagueConfigs) {
            setLeagueConfigs(data?.getLeagueConfigs);
        }
    }, [data?.getLeagueConfigs, setLeagueConfigs]);
};
